import React from "react";
import CardRow, { CardRecord } from "components/card-row/CardRow";
import CardRowSkeleton from "components/card-row/CardRowSkeleton";
import Button from "components/button/Button";
import SearchInput from "components/search-input/SearchInput";
import { useSearch } from "components/search-input/useSearch";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useCardLookup } from "features/cards/useCardLookup";

const CARD_SEARCH_KEYS: (keyof CardRecord)[] = ["cardNumber"];
const FULL_PAN_LENGTH = 16;
const toDigits = (value: string) => value.replace(/\D/g, "");
// Match the recent list on digits so a spaced/formatted PAN (as the UI itself
// renders them) still hits a card that's already loaded, rather than falling
// through to a spurious on-chain lookup.
const CARD_SEARCH_OPTIONS = { normalize: toDigits };

interface Props {
  cards: CardRecord[];
  loading: boolean;
  onIssueCard: () => void;
}

function CardsList({ cards, loading, onIssueCard }: Props) {
  const { query, setQuery, results } = useSearch(
    cards,
    CARD_SEARCH_KEYS,
    CARD_SEARCH_OPTIONS,
  );

  const trimmedQuery = query.trim();
  const digits = toDigits(trimmedQuery);
  // The on-chain lookup resolves a specific card, so it needs the whole PAN.
  const isFullPan = digits.length === FULL_PAN_LENGTH;

  // Only the recent cards are loaded up front; a search miss here is our cue to
  // check the chain — but only once the full 16-digit number has been entered.
  const noLocalMatch =
    !loading && trimmedQuery.length > 0 && results.length === 0;

  // Hold the network lookup until typing settles, so we don't fire a request
  // (and flash "not found") on every keystroke.
  const debouncedDigits = useDebouncedValue(digits, 400);
  const settling = debouncedDigits !== digits;
  const lookup = useCardLookup(
    debouncedDigits,
    noLocalMatch && isFullPan && !settling,
  );

  // While the debounce catches up we're still effectively searching; once it
  // settles, the hook's status drives the outcome (loading/found/miss/error).
  const chainStatus = settling ? "loading" : lookup.status;

  return (
    <>
      <div className="ip-section-header">
        <h3 className="ip-section-title">Cards</h3>
        <div className="ip-section-actions">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search by card number..."
          />
          <Button
            variant="primary"
            size="sm"
            className="ip-section-cta"
            onClick={onIssueCard}
          >
            + Issue card
          </Button>
        </div>
      </div>

      <div className="ip-cards-table">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => <CardRowSkeleton key={i} />)
        ) : results.length > 0 ? (
          results.map((card) => <CardRow key={card.cardNumber} {...card} />)
        ) : trimmedQuery.length === 0 ? (
          <p className="ip-empty">No cards yet.</p>
        ) : !isFullPan ? (
          <p className="ip-empty">
            No matching card. Enter the full 16-digit card number to look it up
            on-chain.
          </p>
        ) : chainStatus === "loading" ? (
          <CardRowSkeleton />
        ) : chainStatus === "found" && lookup.card ? (
          <>
            <p className="ip-lookup-note">Not in recent cards — found on-chain.</p>
            <CardRow {...lookup.card} />
          </>
        ) : chainStatus === "not-found" ? (
          <p className="ip-empty">No card found for “{trimmedQuery}”.</p>
        ) : (
          <div className="ip-lookup-error">
            <p className="ip-lookup-error__msg">
              Couldn’t look up this card right now.
            </p>
            <Button variant="ghost" size="sm" onClick={() => lookup.refetch()}>
              Retry
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

export default CardsList;
